import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../contexts/NotificationContext';
import { useLanguage } from '../../contexts/LanguageContext';
import './NotificationBell.css';

const NotificationBell: React.FC = () => {
    const {
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead,
        clearNotifications,
        permissionStatus,
        requestPermission
    } = useNotification();
    const { t } = useLanguage();
    const navigate = useNavigate();

    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleBellClick = () => {
        setIsOpen(!isOpen);
    };

    const handleNotificationClick = (id: string, type: string, bookingId?: number) => {
        markAsRead(id);
        // Navigate to bookings page for booking notifications
        if (type === 'booking') {
            setIsOpen(false);
            navigate('/bookings');
        }
    };

    const handleEnableNotifications = async () => {
        await requestPermission();
    };

    const formatTimeAgo = (date: Date): string => {
        const now = new Date();
        const diffMs = now.getTime() - new Date(date).getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return t('notifications.time.justNow');
        if (diffMins < 60) return t('notifications.time.minsAgo').replace('{mins}', diffMins.toString());
        if (diffHours < 24) return t('notifications.time.hoursAgo').replace('{hours}', diffHours.toString());
        return t('notifications.time.daysAgo').replace('{days}', diffDays.toString());
    };

    const getNotificationIcon = (type: string): string => {
        switch (type) {
            case 'booking': return '📅';
            case 'success': return '✅';
            case 'warning': return '⚠️';
            case 'info': return 'ℹ️';
            default: return '🔔';
        }
    };

    return (
        <div className="notification-bell-container" ref={dropdownRef}>
            <button
                className="notification-bell-btn"
                onClick={handleBellClick}
                title={t('notifications.title')}
            >
                <span className="bell-icon">🔔</span>
                {unreadCount > 0 && (
                    <span className="notification-badge">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="notification-dropdown">
                    <div className="notification-header">
                        <h4>{t('notifications.title')}</h4>
                        <div className="header-actions">
                            {unreadCount > 0 && (
                                <button
                                    className="mark-all-read-btn"
                                    onClick={markAllAsRead}
                                    title={t('notifications.markAllRead')}
                                >
                                    {t('notifications.markAllRead')}
                                </button>
                            )}
                            {notifications.length > 0 && (
                                <button
                                    className="clear-all-btn"
                                    onClick={clearNotifications}
                                    title={t('notifications.clearAll')}
                                >
                                    🗑️
                                </button>
                            )}
                        </div>
                    </div>

                    {permissionStatus !== 'granted' && permissionStatus !== 'unsupported' && (
                        <div className="permission-banner">
                            <p>{t('notifications.enableBanner')}</p>
                            <button onClick={handleEnableNotifications}>
                                {t('notifications.enableBtn')}
                            </button>
                        </div>
                    )}

                    <div className="notification-list">
                        {notifications.length === 0 ? (
                            <div className="empty-notifications">
                                <span className="empty-icon">📭</span>
                                <p>{t('notifications.empty')}</p>
                            </div>
                        ) : (
                            notifications.slice(0, 10).map(notification => {
                                // For backend messages that are in VN, we can do a quick check to translate if the app is in EN
                                let displayTitle = notification.title;
                                let displayMsg = notification.message;
                                if (t('notifications.title') === 'Notifications') {
                                     displayTitle = displayTitle.replace('Có lịch đặt mới!', 'New online booking!');
                                     displayMsg = displayMsg.replace('Khách hàng:', 'Customer:').replace('SĐT:', 'Phone:').replace('Dịch vụ:', 'Service:').replace('Giờ:', 'Time:');
                                }
                                
                                return (
                                <div
                                    key={notification.id}
                                    className={`notification-item ${notification.read ? 'read' : 'unread'} ${notification.type === 'booking' || String(notification.type) === 'booking_new' ? 'clickable' : ''}`}
                                    onClick={() => handleNotificationClick(notification.id, notification.type, notification.bookingId)}
                                    style={{ cursor: notification.type === 'booking' || String(notification.type) === 'booking_new' ? 'pointer' : 'default' }}
                                >
                                    <div className="notification-icon">
                                        {getNotificationIcon(notification.type)}
                                    </div>
                                    <div className="notification-content">
                                        <div className="notification-title">{displayTitle}</div>
                                        <div className="notification-message" style={{ whiteSpace: 'pre-wrap' }}>{displayMsg}</div>
                                        <div className="notification-meta">
                                            <span className="notification-time">
                                                {formatTimeAgo(notification.timestamp)}
                                            </span>
                                            {(notification.type === 'booking' || String(notification.type) === 'booking_new') && (
                                                <span className="notification-action">
                                                    {t('notifications.action')}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {!notification.read && <div className="unread-dot" />}
                                </div>
                            )})
                        )}
                    </div>

                    {notifications.length > 0 && (
                        <div className="notification-footer">
                            <button
                                className="view-all-btn"
                                onClick={() => { setIsOpen(false); navigate('/notifications'); }}
                            >
                                {t('notifications.viewAll').replace('{count}', notifications.length.toString())}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
